<template>
    <div class="VolunteerProfileForm flex flex-col gap-4">
        <CFormGroup
            v-if="show('interests')"
            description="Slová oddeľuj enterom. Jeden #hashtag (kľúčové slovo) môže obsahovať max. 3 slová (po 35 znakov). Hashtagy sa oddeľujú enterom. Pomocou nich dokáže systém vyhľadávať a odporúčať."
            :error="errors.interests"
            label="S čím viem ako dobrovoľník pomôcť"
            required
        >
            <CInterestsInput
                :disabled="disabled"
                placeholder="Pridaj kľúčové slová…"
                :model-value="modelValue?.interests"
                @update:model-value="update({ interests: $event })"
            />

            <SuggestedKeywords
                class="mt-2"
                :disabled="disabled"
                label="Vyber si z najčastejších:"
                :selected-keywords="modelValue?.interests"
                :suggested-keywords="suggestedKeywords.keywords"
                @select-keyword="addInterest"
            />
        </CFormGroup>

        <CFormGroup v-if="show('helpWhoFields')" :error="errors.helpWho" label="Komu chcem pomáhať">
            <CFormCheckboxGroup
                :disabled="disabled"
                option-label-key="label"
                option-value-key="id"
                :options="helpWhoOptions"
                :model-value="modelValue?.helpWho"
                @update:model-value="update({ helpWho: $event })"
            />
        </CFormGroup>

        <div v-if="show('prefersFieldOfExperience')" class="mb-5 flex flex-col gap-3">
            <CFormGroup
                class="!mb-0 !w-auto"
                description="(práca, podnikanie, dobrovoľníctvo, projekt)"
                :error="errors.prefersFieldOfExperience"
                label="Chcem dobrovoľníčiť v oblasti, v ktorej mám profesnú skúsenosť"
                required
            >
                <CFormRadio
                    :disabled="disabled"
                    option-label-key="label"
                    option-value-key="value"
                    :options="prefersFieldOfExperienceOptions"
                    :model-value="modelValue?.prefersFieldOfExperience"
                    with-description
                    @update:model-value="update({ prefersFieldOfExperience: $event })"
                />
            </CFormGroup>

            <CFormGroup
                v-if="modelValue?.prefersFieldOfExperience === VolunteerPrefersFieldOfExperience.YES"
                class="!mb-0 !w-auto"
                :error="errors.professionalExperienceDetails"
                label="Aké máte skúsenosti? Ako dlho?"
                :label-info="`${getLength(modelValue?.fieldOfExperienceDetails || '')}/300`"
            >
                <CFormTextarea
                    :disabled="disabled"
                    maxlength="300"
                    :model-value="modelValue?.fieldOfExperienceDetails"
                    @update:model-value="update({ fieldOfExperienceDetails: $event })"
                />
            </CFormGroup>
        </div>

        <div v-if="show('capacity')" class="mb-5 lg:flex lg:flex-row">
            <CFormGroup
                class="!mb-0 lg:w-2/5"
                :error="errors.timeCapacity"
                label="Ako dlho môžem na projekte SPOLUpracovať"
            >
                <CFormCheckboxGroup
                    option-label-key="label"
                    option-value-key="id"
                    :options="capacity"
                    :model-value="modelValue?.timeCapacity"
                    @update:model-value="handleTimeCapacity"
                />
            </CFormGroup>

            <CAlert v-if="isOnProfile" class="h-fit lg:w-3/5" variant="info">
                Môžeš vybrať oba. Presnejšie sa dohodnete s organizáciou už na základe konkrétneho projektu.
            </CAlert>
        </div>

        <CFormGroup
            v-if="show('intensityValue') && showInput"
            class="ml-6 max-w-lg"
            :error="errors.intensityValue"
            label="Koľko času viem projektu venovať"
        >
            <div class="grid grid-cols-3 gap-4">
                <CFormInput
                    class="col-span-2"
                    :disabled="disabled"
                    :min-value="1"
                    placeholder="1"
                    step="0.5"
                    type="number"
                    :model-value="modelValue?.intensityValue"
                    @update:model-value="update({ intensityValue: $event })"
                />

                <CFormSelect
                    :disabled="disabled"
                    label-key="label"
                    :options="intensityUnits"
                    :model-value="modelValue?.intensityUnit"
                    value-key="value"
                    @update:model-value="update({ intensityUnit: $event })"
                />
            </div>
        </CFormGroup>

        <CFormGroup v-if="show('cooperation')" :error="errors.cooperation" label="Ako chcem spolupracovať" required>
            <CFormCheckboxGroup
                :disabled="disabled"
                option-label-key="label"
                option-value-key="id"
                :options="cooperationType"
                :model-value="modelValue?.cooperation"
                @update:model-value="handleCooperation"
            />
        </CFormGroup>

        <CFormGroup
            v-if="userProfile.geoCountry === 'Slovensko' && showRegion"
            class="ml-6"
            :error="errors.volunteerGeoRegion"
            label="Kde chceš dobrovoľníčiť?"
            required
        >
            <CFormMultiSelect
                :disabled="disabled"
                :options="slovakRegions"
                placeholder="--- Vyber kraj ---"
                :model-value="geoRegion"
                @update:model-value="handleRegion"
            />
        </CFormGroup>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { ValidationErrors, ValidationResults } from 'last-validator'
import cloneDeep from 'lodash-es/cloneDeep'

import { intensityUnits } from '#shared/types/Project'
import { UserProfile, VolunteerPrefersFieldOfExperience, VolunteerProfile } from '#shared/types/User'
import { capacity, cooperationType, helpWho as helpWhoOptions } from '#shared/types/VolunteerOptions'
import { addArrayItem, getLength, scrollToError } from '#shared/utils'
import { slovakRegions } from '@/constants/slovakRegions'
import SuggestedKeywordsUtils from '@/utils/suggestedKeywords'
import { validateVolunteerProfile } from '@/validators/volunteerProfile'
import type { Option } from '@/components/ui/CFormRadio.vue'

// Define options
const prefersFieldOfExperienceOptions: Option[] = [
    {
        label: 'Nie',
        description: '(napr. chcem sa naučiť niečo iné, chcem si vyvetrať hlavu…)',
        value: VolunteerPrefersFieldOfExperience.NO
    },
    { label: 'Som otvorený/á obom možnostiam', value: VolunteerPrefersFieldOfExperience.EITHER },
    { label: 'Áno', value: VolunteerPrefersFieldOfExperience.YES }
]

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
    modelValue: {
        type: Object as () => VolunteerProfile,
        required: true
    },
    onlyMissingFields: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    missingFields: {
        type: Array as () => string[],
        default: () => []
    },
    isOnProfile: {
        type: Boolean,
        default: false
    }
})

// Use store
const store = useApplicationStore()

// Setup reactive state
const userProfile = ref<UserProfile>({})
const errors = ref<ValidationErrors>({})
const showInput = ref(false)
const showRegion = ref(false)

// Initialize suggested keywords
const suggestedKeywords = ref(new SuggestedKeywordsUtils('volunteerProfileForm', store.APPLICATION_USER_PROFILE))

// Computed properties
const geoRegion = computed((): string | string[] | undefined => {
    return props.modelValue?.volunteerGeoRegion || userProfile.value.geoRegion
})

// Watchers
watch(() => suggestedKeywords.value.featuredKeywords, () => {
    suggestedKeywords.value.resolveFeatured(props.modelValue?.interests || [], addInterest)
})

// Lifecycle hooks
onMounted(() => {
    userProfile.value = cloneDeep(store.APPLICATION_USER_PROFILE || {})

    if (props.modelValue?.timeCapacity?.includes('long_term')) {
        showInput.value = true
    }
    if (props.modelValue?.volunteerGeoRegion) {
        showRegion.value = true
    }
})

// Methods
const show = (field: string): boolean => {
    return !props.onlyMissingFields || props.missingFields.includes(field)
}

const update = (changes: { [key in keyof Partial<VolunteerProfile>]: any }) => {
    emit('update:modelValue', { ...props.modelValue, ...changes })
}

const addInterest = (interest: string) => {
    const interests = addArrayItem(props.modelValue?.interests || [], interest) as string[]
    update({ interests })
}

const handleTimeCapacity = ($event: any) => {
    update({ timeCapacity: $event })
    showInput.value = $event.includes('long_term')
}

const handleCooperation = ($event: any) => {
    update({ cooperation: $event })
    showRegion.value = $event.includes('personal')
}

const handleRegion = ($event: any) => {
    update({ volunteerGeoRegion: $event })
}

const validate = async (): Promise<ValidationResults> => {
    const result = await validateVolunteerProfile(props.modelValue || {} as VolunteerProfile)
    errors.value = result.errors

    if (!result.isValid) {
        nextTick(() => scrollToError())
    }

    return result
}

// Expose required methods
defineExpose({
    validate
})
</script>